import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2, Check, Search, Sparkles, Palette, Star, ChevronRight, Info, Eye, ChevronDown, LogOut, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import ThemePreviewModal from '@/components/ThemePreviewModal';
import WowCXLogo from '@/components/WowCXLogo';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

// Number of themes to show initially and load more each time
const THEMES_PER_PAGE = 6;

const ThemeSelectionPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedWpOrgTheme, setSelectedWpOrgTheme] = useState(null); // WordPress.org theme selection
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshingThemes, setRefreshingThemes] = useState(false);
  
  // Pagination state for theme library
  const [visibleCount, setVisibleCount] = useState(THEMES_PER_PAGE);
  
  // Theme Intelligence state
  const [recommendations, setRecommendations] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(true);
  const [recommendationsFailed, setRecommendationsFailed] = useState(false);
  const [betterThemesFromWpOrg, setBetterThemesFromWpOrg] = useState([]);
  
  const [loadingWpOrgThemes, setLoadingWpOrgThemes] = useState(false);
  const [wpOrgSearchFailed, setWpOrgSearchFailed] = useState(false);
  
  // Theme Preview Modal state
  const [previewTheme, setPreviewTheme] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  
  const openThemePreview = (theme, e) => {
    e.stopPropagation(); // Prevent card selection
    setPreviewTheme(theme);
    setIsPreviewOpen(true);
  };
  
  const closeThemePreview = () => {
    setIsPreviewOpen(false);
    setPreviewTheme(null);
  };
  
  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(THEMES_PER_PAGE);
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    fetchThemes();
    // Small delay before fetching recommendations to allow project creation to complete
    const timer = setTimeout(() => {
      fetchRecommendations();
    }, 500);
    return () => clearTimeout(timer);
  }, [projectId]);

  const fetchThemes = async () => {
    try {
      const response = await axios.get(`${API}/themes`);
      setThemes(response.data);
    } catch (error) {
      toast.error('Failed to load themes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async (retryCount = 0) => {
    try {
      // STEP 1: Fetch LOCAL recommendations quickly (no WordPress.org search)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second timeout for local
      
      console.log('[Recommendations] Fetching LOCAL recommendations for project:', projectId);
      const response = await axios.get(
        `${API}/projects/${projectId}/theme-recommendations/local`,
        { 
          withCredentials: true,
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);
      
      console.log('[Recommendations] Local response received:', {
        status: response.status,
        localCount: response.data.recommendations?.length || 0,
        businessCategories: response.data.business_categories,
        brandColors: response.data.brand_colors?.palette?.length || 0
      });
      
      // Debug: Log the actual recommendations data
      if (response.data.recommendations?.length > 0) {
        console.log('[Recommendations] Top recommendation:', response.data.recommendations[0]);
      } else {
        console.log('[Recommendations] WARNING: No local recommendations returned!');
      }
      
      setRecommendations(response.data);
      setRecommendationsFailed(false);
      setLoadingRecommendations(false);
      
      // STEP 2: Fetch WordPress.org recommendations ASYNCHRONOUSLY (don't block UI)
      fetchWordPressOrgThemes();
      
    } catch (error) {
      console.error('[Recommendations] Failed to fetch:', error?.message || error?.toString() || error);
      console.error('[Recommendations] Error details:', {
        name: error?.name,
        code: error?.code,
        status: error?.response?.status,
        data: error?.response?.data,
        url: `${API}/projects/${projectId}/theme-recommendations/local`
      });
      
      // Handle abort/timeout
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
        console.log('[Recommendations] Request timed out after 20s');
        setRecommendationsFailed(true);
        setLoadingRecommendations(false);
        return;
      }
      
      // Handle authentication errors - redirect to login
      if (error.response?.status === 401) {
        console.log('[Recommendations] Authentication failed - session may be expired');
        toast.error('Your session has expired. Please log in again.');
        // Clear invalid token
        localStorage.removeItem('session_token');
        // Small delay before redirect
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
        return;
      }
      
      // Retry up to 3 times with delay if project not found (race condition after creation)
      if (error.response?.status === 404 && retryCount < 3) {
        console.log(`[Recommendations] Retrying fetch (attempt ${retryCount + 2}/4)...`);
        setTimeout(() => fetchRecommendations(retryCount + 1), 1000);
        return;
      }
      // On final failure, mark as failed so we show fallback UI
      console.log('[Recommendations] Final failure, showing fallback UI');
      setRecommendationsFailed(true);
      setLoadingRecommendations(false);
    }
  };
  
  // Fetch WordPress.org themes asynchronously (doesn't block initial UI)
  const fetchWordPressOrgThemes = async () => {
    try {
      setLoadingWpOrgThemes(true);
      setWpOrgSearchFailed(false);
      console.log('[WP.org Themes] Fetching WordPress.org recommendations...');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout (reduced)
      
      const response = await axios.get(
        `${API}/projects/${projectId}/theme-recommendations/wordpress-org`,
        { 
          withCredentials: true,
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);
      
      if (response.data.better_themes_from_wordpress_org?.length > 0) {
        setBetterThemesFromWpOrg(response.data.better_themes_from_wordpress_org);
        console.log('[WP.org Themes] Found:', response.data.better_themes_from_wordpress_org.length, 'themes');
      } else {
        console.log('[WP.org Themes] No WordPress.org themes found');
        setWpOrgSearchFailed(true); // Mark as "no results" rather than just hiding
      }
    } catch (error) {
      console.log('[WP.org Themes] Failed to fetch:', error?.message);
      setWpOrgSearchFailed(true);
    } finally {
      setLoadingWpOrgThemes(false);
    }
  };
  
  // Generate fallback recommendations from loaded themes when API fails
  const getFallbackRecommendations = () => {
    if (!themes || themes.length === 0) return [];
    
    // Pick diverse themes from different categories
    const categories = [...new Set(themes.map(t => t.category).filter(Boolean))];
    const selected = [];
    
    // Try to get one theme from each of the first 4 categories
    for (const category of categories.slice(0, 4)) {
      const categoryThemes = themes.filter(t => t.category === category);
      if (categoryThemes.length > 0) {
        selected.push({
          ...categoryThemes[0],
          score: 75,
          reasons: [`Great for ${category} websites`, 'Popular choice', 'Easy to customize']
        });
      }
      if (selected.length >= 4) break;
    }
    
    // If we still don't have enough, add more from the beginning
    while (selected.length < 4 && themes.length > selected.length) {
      const nextTheme = themes.find(t => !selected.some(s => s.theme_id === t.theme_id));
      if (nextTheme) {
        selected.push({
          ...nextTheme,
          score: 70,
          reasons: ['Versatile design', 'Easy to customize']
        });
      } else {
        break;
      }
    }
    
    return selected;
  };

  const refreshThemesFromWordPress = async () => {
    setRefreshingThemes(true);
    setLoadingRecommendations(true);
    setBetterThemesFromWpOrg([]);
    try {
      // Force refresh themes from WordPress
      await axios.post(`${API}/themes/refresh`);
      // Fetch updated themes
      const response = await axios.get(`${API}/themes?refresh=true`);
      setThemes(response.data);
      // Re-fetch local recommendations with force refresh
      const recResponse = await axios.get(
        `${API}/projects/${projectId}/theme-recommendations/local?refresh=true`,
        { withCredentials: true }
      );
      setRecommendations(recResponse.data);
      setLoadingRecommendations(false);
      
      // Async fetch WordPress.org themes
      fetchWordPressOrgThemes();
      
      toast.success('Themes and recommendations refreshed');
    } catch (error) {
      toast.error('Failed to refresh themes');
      console.error(error);
    } finally {
      setRefreshingThemes(false);
      setLoadingRecommendations(false);
    }
  };

  // Select a WordPress.org theme (creates installation request for admin)
  const handleSelectWpOrgTheme = async (theme) => {
    // Deselect any local theme
    setSelectedTheme(null);
    setSelectedWpOrgTheme(theme);
    toast.success(`Selected "${theme.name}" from WordPress.org`);
  };

  // Get unique categories from all theme tags (not just primary)
  const categories = React.useMemo(() => {
    const allTags = new Set(['All']);
    themes.forEach(t => {
      // Add primary category
      if (t.category) allTags.add(t.category);
      // Add all category_tags
      if (t.category_tags && Array.isArray(t.category_tags)) {
        t.category_tags.forEach(tag => allTags.add(tag));
      }
    });
    return Array.from(allTags).sort((a, b) => {
      if (a === 'All') return -1;
      if (b === 'All') return 1;
      return a.localeCompare(b);
    });
  }, [themes]);

  // Filter themes based on category (matches ANY tag) and search
  const filteredThemes = themes.filter(theme => {
    // Match if selected category is 'All', or if the theme has the category in its tags
    const matchesCategory = selectedCategory === 'All' || 
      theme.category === selectedCategory ||
      (theme.category_tags && theme.category_tags.includes(selectedCategory));
    const matchesSearch = !searchQuery || 
      theme.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      theme.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get recommended theme IDs
  const recommendedThemeIds = recommendations?.recommendations?.map(r => r.theme_id) || [];

  // Helper function to poll generation status
  const pollGenerationStatus = async (jobId) => {
    const maxAttempts = 90; // Poll for up to 3 minutes (90 * 2 seconds)
    let attempts = 0;
    let lastProgress = '';
    
    while (attempts < maxAttempts) {
      try {
        const statusResponse = await axios.get(
          `${API}/ai/generate-design/status/${jobId}`,
          { withCredentials: true }
        );
        
        const { status, progress, design_id, design, error } = statusResponse.data;
        
        // Show progress updates to user via toast (only if changed)
        if (progress && progress !== lastProgress) {
          console.log('[Design Generation]', progress);
          // Show toast for significant progress updates
          if (progress.includes('Generating design') || progress.includes('Processing') || progress.includes('Saving')) {
            toast.loading(progress, { id: 'generation-progress' });
          }
          lastProgress = progress;
        }
        
        if (status === 'completed') {
          toast.dismiss('generation-progress');
          return { status, design_id, design };
        } else if (status === 'failed') {
          toast.dismiss('generation-progress');
          return { status, error };
        }
        
        // Wait 2 seconds before next poll
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      } catch (pollError) {
        console.error('Poll error:', pollError);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    toast.dismiss('generation-progress');
    return { status: 'failed', error: 'Generation timed out after 3 minutes' };
  };

  const handleGenerateDesign = async () => {
    // Check if either a local theme OR a WordPress.org theme is selected
    if (!selectedTheme && !selectedWpOrgTheme) {
      toast.error('Please select a theme');
      return;
    }

    setGenerating(true);

    try {
      // If WordPress.org theme selected, create installation request for admin
      if (selectedWpOrgTheme) {
        // First, create the theme installation request
        await axios.post(
          `${API}/themes/request-installation`,
          {
            theme_slug: selectedWpOrgTheme.slug,
            theme_name: selectedWpOrgTheme.name,
            theme_url: `https://wordpress.org/themes/${selectedWpOrgTheme.slug}/`,
            project_id: projectId,
            screenshot_url: selectedWpOrgTheme.thumbnail
          },
          { withCredentials: true }
        );
        
        // Generate design using the WordPress.org theme (returns job_id immediately)
        const response = await axios.post(
          `${API}/ai/generate-design`,
          {
            project_id: projectId,
            theme_id: `wp_org_${selectedWpOrgTheme.slug}`,
            wp_org_theme: {
              slug: selectedWpOrgTheme.slug,
              name: selectedWpOrgTheme.name,
              screenshot_url: selectedWpOrgTheme.thumbnail,
              preview_url: selectedWpOrgTheme.preview_url,
              color_scheme: selectedWpOrgTheme.color_scheme || {
                primary: '#1A1A1A',
                secondary: '#FFFFFF', 
                accent: '#4F46E5'
              }
            }
          },
          { withCredentials: true }
        );

        // Backend returns job_id - poll for completion
        if (response.data.job_id) {
          toast.loading('Starting design generation...', { id: 'generation-progress' });
          const result = await pollGenerationStatus(response.data.job_id);
          if (result.status === 'completed') {
            toast.success('Design generated! Admin will be notified to install the theme.');
            navigate(`/preview/${projectId}?design=${result.design_id}`);
          } else {
            throw new Error(result.error || 'Generation failed');
          }
        } else if (response.data.design_id) {
          // Legacy direct response (backward compatibility)
          toast.success('Design generated! Admin will be notified to install the theme.');
          navigate(`/preview/${projectId}?design=${response.data.design_id}`);
        } else {
          throw new Error('Unexpected response from server');
        }
      } else {
        // Local theme selection (returns job_id immediately)
        const response = await axios.post(
          `${API}/ai/generate-design`,
          {
            project_id: projectId,
            theme_id: selectedTheme
          },
          { withCredentials: true }
        );

        // Backend returns job_id - poll for completion
        if (response.data.job_id) {
          toast.loading('Starting design generation...', { id: 'generation-progress' });
          const result = await pollGenerationStatus(response.data.job_id);
          if (result.status === 'completed') {
            toast.success('Design generated successfully!');
            navigate(`/preview/${projectId}?design=${result.design_id}`);
          } else {
            throw new Error(result.error || 'Generation failed');
          }
        } else if (response.data.design_id) {
          // Legacy direct response
          toast.success('Design generated successfully!');
          navigate(`/preview/${projectId}?design=${response.data.design_id}`);
        } else {
          throw new Error('Unexpected response from server');
        }
      }
    } catch (error) {
      console.error('Design generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate design');
    } finally {
      setGenerating(false);
    }
  };

  // Handle selecting a local theme (deselects WordPress.org theme)
  const handleSelectLocalTheme = (themeId) => {
    setSelectedWpOrgTheme(null);
    setSelectedTheme(themeId);
  };

  // Helper function to convert score to friendly label
  const getMatchLabel = (score, index, isColorMatch = false) => {
    if (index === 0) return { label: 'Best Match', color: 'bg-green-600 text-white' };
    if (isColorMatch) return { label: 'Color Match', color: 'bg-purple-100 text-purple-700' };
    if (score >= 60) return { label: 'Great Match', color: 'bg-green-100 text-green-700' };
    if (score >= 40) return { label: 'Good Match', color: 'bg-amber-100 text-amber-700' };
    return { label: 'Suggested', color: 'bg-zinc-100 text-zinc-600' };
  };

  // Progress steps for loading animation
  const [currentStep, setCurrentStep] = useState(0);
  const progressSteps = [
    { text: "Reading your business profile...", icon: "📋" },
    { text: "Extracting colors from your logo...", icon: "🎨" },
    { text: "Analyzing your industry keywords...", icon: "🔍" },
    { text: "Searching our theme library...", icon: "📚" },
    { text: "Checking WordPress.org for matches...", icon: "🌐" },
    { text: "Calculating compatibility scores...", icon: "⚡" },
    { text: "Preparing your recommendations...", icon: "✨" }
  ];

  // Animate through steps during loading
  useEffect(() => {
    if (loading || loadingRecommendations) {
      const interval = setInterval(() => {
        setCurrentStep(prev => (prev + 1) % progressSteps.length);
      }, 1800);
      return () => clearInterval(interval);
    } else {
      setCurrentStep(0);
    }
  }, [loading, loadingRecommendations, progressSteps.length]);

  // Loading state - ONLY block on themes loading, NOT recommendations
  // Recommendations can load in background while user browses themes
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center px-4">
        {/* Main spinner */}
        <div className="relative mb-8">
          <div className="w-24 h-24 rounded-full border-4 border-amber-100 border-t-amber-600 animate-spin"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl">
            {progressSteps[currentStep].icon}
          </div>
        </div>
        
        {/* Title */}
        <h2 className="text-xl font-semibold text-zinc-900 mb-2" style={{fontFamily: 'Outfit'}}>
          Loading themes...
        </h2>
        
        {/* Current step text */}
        <div className="h-8 flex items-center justify-center">
          <p className="text-sm text-amber-600 font-medium animate-pulse">
            {progressSteps[currentStep].text}
          </p>
        </div>
        
        {/* Progress dots */}
        <div className="flex gap-2 mt-6">
          {progressSteps.map((_, idx) => (
            <div 
              key={idx}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                idx === currentStep 
                  ? 'bg-amber-500 w-6' 
                  : idx < currentStep 
                    ? 'bg-amber-400' 
                    : 'bg-zinc-300'
              }`}
            />
          ))}
        </div>
        
        {/* Subtle message */}
        <p className="mt-8 text-xs text-zinc-400 max-w-md text-center">
          Loading theme library...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-6 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <WowCXLogo size="md" />
            <div className="h-6 w-px bg-zinc-200"></div>
            <div>
              <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit'}}>Choose Your Theme</h1>
              <p className="text-xs text-zinc-500">Select a WordPress theme that matches your style</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="dashboard-link-btn"
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              My Projects
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
                  navigate('/');
                } catch (err) {
                  navigate('/');
                }
              }}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-7xl mx-auto">

          {/* Brand Colors & AI Recommendations Section */}
          {loadingRecommendations && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-10"
            >
              {/* Loading Skeleton for Recommendations */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 animate-pulse">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="h-5 bg-amber-200/50 rounded w-48 mb-2 animate-pulse"></div>
                    <div className="h-4 bg-amber-200/30 rounded w-64 animate-pulse"></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-white rounded-xl p-4 border border-amber-100">
                      <div className="h-48 bg-zinc-100 rounded-lg animate-pulse mb-4"></div>
                      <div className="h-4 bg-zinc-100 rounded w-3/4 mb-2 animate-pulse"></div>
                      <div className="h-3 bg-zinc-100 rounded w-full animate-pulse"></div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-sm text-amber-600 mt-4 animate-pulse">
                  Finding AI recommendations for your business...
                </p>
              </div>
            </motion.div>
          )}
          
          {/* Fallback Recommendations when API fails */}
          {!loadingRecommendations && recommendationsFailed && themes.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-10"
            >
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-zinc-900" style={{fontFamily: 'Outfit'}}>
                      Popular Theme Picks
                    </h2>
                    <p className="text-sm text-zinc-600">
                      Here are some versatile themes to get you started
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setLoadingRecommendations(true);
                      setRecommendationsFailed(false);
                      fetchRecommendations();
                    }}
                    className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Retry AI
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {getFallbackRecommendations().map((theme, index) => (
                    <Card
                      key={theme.theme_id}
                      data-testid={`fallback-theme-${theme.theme_id}`}
                      className={`cursor-pointer overflow-hidden bg-white hover:shadow-lg transition-all duration-300 ${
                        selectedTheme === theme.theme_id 
                          ? 'ring-2 ring-amber-500 shadow-lg' 
                          : 'hover:ring-1 hover:ring-amber-300'
                      }`}
                      onClick={() => handleSelectLocalTheme(theme.theme_id)}
                    >
                      <CardContent className="p-0">
                        <div className="relative bg-zinc-100">
                          <img
                            src={theme.thumbnail}
                            alt={theme.name}
                            className="w-full h-32 object-cover"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600';
                            }}
                          />
                          {selectedTheme === theme.theme_id && (
                            <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                          {index === 0 && (
                            <div className="absolute top-2 left-2 px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded">
                              Popular
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <h3 className="font-semibold text-sm text-zinc-900 truncate">{theme.name}</h3>
                          <p className="text-xs text-zinc-500">{theme.category}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
          
          {!loadingRecommendations && recommendations && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-10"
            >
              {/* Brand Color Palette */}
              {recommendations.brand_colors?.palette?.length > 0 && (
                <div className="bg-white rounded-2xl border border-zinc-200 p-6 mb-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <Palette className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-zinc-900" style={{fontFamily: 'Outfit'}}>
                        Your Brand Color Palette
                      </h2>
                      <p className="text-sm text-zinc-600">
                        Extracted from your {recommendations.brand_colors.sources?.join(' & ')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Color Swatches */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    {recommendations.brand_colors.palette.map((color, i) => (
                      <div key={i} className="flex flex-col items-center">
                        <div 
                          className="w-14 h-14 rounded-xl shadow-md border border-zinc-200"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                        <span className="text-xs text-zinc-500 mt-1 font-mono">{color}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Info Note */}
                  <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      <strong>Smart Color Blending:</strong> We&apos;ll intelligently combine your brand colors with the selected theme&apos;s color palette to create a cohesive design that maintains the theme&apos;s appeal while incorporating your brand identity.
                    </p>
                  </div>
                </div>
              )}
              
              {/* AI Recommended Themes - ALWAYS show if we have recommendations */}
              {recommendations.recommendations?.length > 0 && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-zinc-900" style={{fontFamily: 'Outfit'}}>
                        AI Recommended Themes for You
                      </h2>
                      <p className="text-sm text-zinc-600">
                        Based on your business: {recommendations.specific_keywords?.length > 0 
                          ? recommendations.specific_keywords.slice(0, 3).join(', ')
                          : recommendations.business_categories?.join(', ')}
                      </p>
                    </div>
                    {/* Subtle Refresh Button */}
                    <button
                      onClick={refreshThemesFromWordPress}
                      disabled={refreshingThemes}
                      className="text-xs text-zinc-400 hover:text-amber-600 transition-colors flex items-center gap-1"
                      data-testid="refresh-themes-btn"
                      title="Refresh themes from WordPress"
                    >
                      <RefreshCw className={`w-3 h-3 ${refreshingThemes ? 'animate-spin' : ''}`} />
                      <span className="hidden sm:inline">{refreshingThemes ? 'Refreshing...' : 'Refresh'}</span>
                    </button>
                  </div>
                  
                  {/* Recommended Theme Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {recommendations.recommendations.map((rec, index) => {
                      // API returns flat object with theme_id, name, etc. directly (not nested in .theme)
                      const themeData = rec;
                      const fullTheme = themes.find(t => t.theme_id === themeData.theme_id);
                      const description = fullTheme?.description || themeData.description || `${themeData.name} - A WordPress theme for ${themeData.category} websites.`;
                      const colorScheme = fullTheme?.color_scheme || themeData.color_scheme;
                      
                      return (
                        <motion.div
                          key={themeData.theme_id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Card
                            data-testid={`recommended-theme-${themeData.theme_id}`}
                            className={`cursor-pointer overflow-hidden bg-white hover:shadow-xl transition-all duration-300 ${
                              selectedTheme === themeData.theme_id 
                                ? 'ring-2 ring-amber-500 shadow-lg' 
                                : 'hover:ring-2 hover:ring-amber-300'
                            }`}
                            onClick={() => handleSelectLocalTheme(themeData.theme_id)}
                          >
                            <CardContent className="p-0">
                              {/* Theme Screenshot */}
                              <div className="relative bg-zinc-100 group">
                                <img
                                  src={themeData.thumbnail}
                                  alt={themeData.name}
                                  className="w-full object-contain"
                                  style={{ height: '220px' }}
                                  onError={(e) => {
                                    e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600';
                                  }}
                                />
                                {/* Preview Button - appears on hover */}
                                <button
                                  onClick={(e) => openThemePreview({
                                    ...themeData,
                                    ...fullTheme,
                                    theme_id: themeData.theme_id,
                                    description: description
                                  }, e)}
                                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center"
                                  data-testid={`preview-recommended-theme-${themeData.theme_id}`}
                                >
                                  <div className="bg-white rounded-full px-4 py-2 flex items-center gap-2 shadow-lg hover:bg-zinc-100 transition">
                                    <Eye className="w-5 h-5 text-amber-600" />
                                    <span className="font-semibold text-zinc-900">Preview Full</span>
                                  </div>
                                </button>
                                {selectedTheme === themeData.theme_id && (
                                  <div className="absolute top-3 right-3 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-lg z-10">
                                    <Check className="w-6 h-6 text-white" />
                                  </div>
                                )}
                                {/* AI Badge */}
                                <div className="absolute top-3 left-3 flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full text-xs font-bold text-white shadow-md z-10">
                                  <Star className="w-3 h-3" />
                                  #{index + 1} Pick
                                </div>
                              </div>
                              
                              {/* Theme Info */}
                              <div className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <h3 className="font-bold text-zinc-900">{themeData.name}</h3>
                                    <p className="text-xs text-zinc-500">{themeData.category}</p>
                                  </div>
                                  <div className="text-right">
                                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getMatchLabel(rec.score, index, rec.color_match).color}`}>
                                      {getMatchLabel(rec.score, index, rec.color_match).label}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Theme Description */}
                                <p className="text-sm text-zinc-600 line-clamp-2 mt-2">
                                  {description}
                                </p>
                                
                                {/* Match Reasons */}
                                <div className="flex flex-wrap gap-1 mt-3">
                                  {rec.reasons?.slice(0, 2).map((reason, i) => (
                                    <span key={i} className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full">
                                      ✓ {reason}
                                    </span>
                                  ))}
                                </div>
                                
                                {/* Theme Colors Preview */}
                                {colorScheme && (
                                  <div className="flex gap-1.5 mt-3">
                                    {/* Show palette colors if available */}
                                    {colorScheme.palette?.length > 0 ? (
                                      colorScheme.palette.slice(0, 5).map((color, idx) => (
                                        <div 
                                          key={idx}
                                          className="w-5 h-5 rounded-full border border-zinc-200 shadow-sm"
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))
                                    ) : (
                                      <>
                                        <div 
                                          className="w-5 h-5 rounded-full border border-zinc-200 shadow-sm"
                                          style={{ backgroundColor: colorScheme.primary }}
                                          title="Primary"
                                        />
                                        <div 
                                          className="w-5 h-5 rounded-full border border-zinc-200 shadow-sm"
                                          style={{ backgroundColor: colorScheme.accent }}
                                          title="Accent"
                                        />
                                        <div 
                                          className="w-5 h-5 rounded-full border border-zinc-200 shadow-sm"
                                          style={{ backgroundColor: colorScheme.secondary }}
                                          title="Secondary"
                                        />
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Fallback when recommendations API returned but with no recommendations */}
              {recommendations && !recommendations.recommendations?.length && themes.length > 0 && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-bold text-zinc-900" style={{fontFamily: 'Outfit'}}>
                        Popular Theme Picks
                      </h2>
                      <p className="text-sm text-zinc-600">
                        Here are some versatile themes from our library
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {getFallbackRecommendations().map((theme, index) => (
                      <Card
                        key={theme.theme_id}
                        data-testid={`fallback-theme-${theme.theme_id}`}
                        className={`cursor-pointer overflow-hidden bg-white hover:shadow-lg transition-all duration-300 ${
                          selectedTheme === theme.theme_id 
                            ? 'ring-2 ring-amber-500 shadow-lg' 
                            : 'hover:ring-1 hover:ring-amber-300'
                        }`}
                        onClick={() => handleSelectLocalTheme(theme.theme_id)}
                      >
                        <CardContent className="p-0">
                          <div className="relative bg-zinc-100">
                            <img
                              src={theme.thumbnail}
                              alt={theme.name}
                              className="w-full h-32 object-cover"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600';
                              }}
                            />
                            {selectedTheme === theme.theme_id && (
                              <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            )}
                            {index === 0 && (
                              <div className="absolute top-2 left-2 px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded">
                                Popular
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            <h3 className="font-semibold text-sm text-zinc-900 truncate">{theme.name}</h3>
                            <p className="text-xs text-zinc-500">{theme.category}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Recommended Themes from WordPress.org - Shows loading or content */}
          {(loadingWpOrgThemes || betterThemesFromWpOrg.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                    {loadingWpOrgThemes ? (
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Search className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-zinc-900" style={{fontFamily: 'Outfit'}}>
                      Recommended Themes from WordPress.org
                    </h2>
                    <p className="text-sm text-zinc-600">
                      {loadingWpOrgThemes 
                        ? 'Searching WordPress.org for matching themes...' 
                        : 'We found these themes that match your business and brand colors'}
                    </p>
                  </div>
                </div>
                
                {loadingWpOrgThemes ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[1, 2].map((i) => (
                      <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                        <div className="h-48 bg-zinc-200 rounded-lg mb-4"></div>
                        <div className="h-5 bg-zinc-200 rounded w-2/3 mb-2"></div>
                        <div className="h-4 bg-zinc-200 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {betterThemesFromWpOrg.map((theme, index) => (
                    <motion.div
                      key={theme.theme_id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card 
                        className={`cursor-pointer overflow-hidden bg-white hover:shadow-xl transition-all duration-300 ${
                          selectedWpOrgTheme?.slug === theme.slug 
                            ? 'ring-2 ring-amber-500 shadow-lg' 
                            : 'hover:ring-2 hover:ring-amber-300'
                        }`}
                        onClick={() => handleSelectWpOrgTheme(theme)}
                      >
                        <CardContent className="p-0">
                          {/* Theme Screenshot */}
                          <div className="relative bg-zinc-100 group">
                            {theme.thumbnail && (
                              <img 
                                src={theme.thumbnail} 
                                alt={theme.name}
                                className="w-full object-contain"
                                style={{ height: '220px' }}
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600';
                                }}
                              />
                            )}
                            {/* Preview Button - appears on hover */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Open in-app preview modal for consistent experience
                                openThemePreview({
                                  theme_id: `wp_org_${theme.slug}`,
                                  name: theme.name,
                                  slug: theme.slug,
                                  thumbnail: theme.thumbnail || theme.screenshot_url,
                                  description: theme.description,
                                  preview_url: theme.preview_url,
                                  homepage: theme.homepage,
                                  author: theme.author,
                                  rating: theme.rating,
                                  is_wordpress_org: true,
                                  color_scheme: theme.color_scheme || {
                                    primary: '#3b82f6',
                                    secondary: '#6366f1'
                                  }
                                }, e);
                              }}
                              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center"
                              data-testid={`preview-wp-org-theme-${theme.slug}`}
                            >
                              <div className="bg-white rounded-full px-4 py-2 flex items-center gap-2 shadow-lg hover:bg-zinc-100 transition">
                                <Eye className="w-5 h-5 text-amber-600" />
                                <span className="font-semibold text-zinc-900">Preview Full</span>
                              </div>
                            </button>
                            {/* Selected Checkmark */}
                            {selectedWpOrgTheme?.slug === theme.slug && (
                              <div className="absolute top-3 right-3 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-lg z-10">
                                <Check className="w-6 h-6 text-white" />
                              </div>
                            )}
                            {/* WordPress.org Badge */}
                            <div className="absolute top-3 left-3 flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-xs font-bold text-white shadow-md z-10">
                              <ExternalLink className="w-3 h-3" />
                              WordPress.org
                            </div>
                          </div>
                          
                          {/* Theme Info */}
                          <div className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="font-bold text-zinc-900">{theme.name}</h3>
                                <p className="text-xs text-zinc-500">{theme.category}</p>
                              </div>
                              <div className="text-right">
                                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getMatchLabel(theme.score, index).color}`}>
                                  {getMatchLabel(theme.score, index).label}
                                </span>
                              </div>
                            </div>
                            
                            {/* Theme Description */}
                            <p className="text-sm text-zinc-600 line-clamp-2 mt-2">
                              {theme.description || `${theme.name} - A WordPress theme perfect for your business.`}
                            </p>
                            
                            {/* Match Reasons */}
                            <div className="flex flex-wrap gap-1 mt-3">
                              {theme.reasons?.slice(0, 2).map((reason, i) => (
                                <span key={i} className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded-full">
                                  ✓ {reason}
                                </span>
                              ))}
                            </div>
                            
                            {/* Brand Color Match Indicator */}
                            {recommendations?.brand_colors?.palette?.length > 0 && (
                              <div className="flex items-center gap-2 mt-3">
                                <span className="text-xs text-zinc-500">Your brand colors:</span>
                                <div className="flex gap-1">
                                  {recommendations.brand_colors.palette.slice(0, 4).map((color, idx) => (
                                    <div 
                                      key={idx}
                                      className="w-4 h-4 rounded-full border border-zinc-200 shadow-sm"
                                      style={{ backgroundColor: color }}
                                      title={color}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Divider - show library section header */}
          {!refreshingThemes && (
            <div className="flex items-center gap-4 mb-8">
              <div className="flex-1 h-px bg-zinc-200"></div>
              <span className="text-sm text-zinc-500 font-medium">
                {loadingRecommendations ? 'Browse our theme library' : 'Or pick from our growing library of themes'}
              </span>
              <div className="flex-1 h-px bg-zinc-200"></div>
            </div>
          )}

          {/* Search and Filter */}
          <div className="mb-8 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                data-testid="theme-search-input"
                placeholder="Search themes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <Button
                  key={category}
                  data-testid={`category-filter-${category}`}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category 
                    ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                    : 'text-zinc-700 hover:bg-zinc-100'
                  }
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Theme Count */}
          <div className="mb-6 text-sm text-zinc-500">
            Showing {Math.min(visibleCount, filteredThemes.length)} of {themes.length} themes
          </div>

          {/* Theme Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {filteredThemes.slice(0, visibleCount).map((theme, index) => (
              <motion.div
                key={theme.theme_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
              >
                <Card
                  data-testid={`theme-card-${theme.theme_id}`}
                  className={`cursor-pointer overflow-hidden bg-white hover:shadow-xl transition-all duration-300 ${
                    selectedTheme === theme.theme_id ? 'ring-2 ring-amber-500 shadow-lg' : 'hover:ring-1 hover:ring-zinc-200'
                  }`}
                  onClick={() => handleSelectLocalTheme(theme.theme_id)}
                >
                  <CardContent className="p-0">
                    {/* Theme Screenshot */}
                    <div className="relative bg-zinc-100 group">
                      <img
                        src={theme.thumbnail}
                        alt={theme.name}
                        className="w-full object-contain"
                        style={{ height: '280px', maxHeight: '280px' }}
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600';
                        }}
                      />
                      {/* Preview Button - appears on hover */}
                      <button
                        onClick={(e) => openThemePreview(theme, e)}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center"
                        data-testid={`preview-theme-${theme.theme_id}`}
                      >
                        <div className="bg-white rounded-full px-4 py-2 flex items-center gap-2 shadow-lg hover:bg-zinc-100 transition">
                          <Eye className="w-5 h-5 text-amber-600" />
                          <span className="font-semibold text-zinc-900">Preview Full</span>
                        </div>
                      </button>
                      {selectedTheme === theme.theme_id && (
                        <div className="absolute top-3 right-3 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-lg z-10">
                          <Check className="w-6 h-6 text-white" />
                        </div>
                      )}
                      {/* Category Badge - Show matched tag when filtering */}
                      <div className="absolute top-3 left-3 flex flex-wrap gap-1 z-10">
                        <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-semibold text-zinc-700 shadow-sm">
                          {theme.category}
                        </span>
                        {/* Show matched tag if filtering by a category that's not the primary */}
                        {selectedCategory !== 'All' && 
                         selectedCategory !== theme.category && 
                         theme.category_tags?.includes(selectedCategory) && (
                          <span className="px-2 py-1.5 bg-amber-500 text-white rounded-full text-xs font-semibold shadow-sm">
                            + {selectedCategory}
                          </span>
                        )}
                      </div>
                      {/* AI Recommended Badge */}
                      {recommendedThemeIds.includes(theme.theme_id) && (
                        <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full text-xs font-bold text-white shadow-md z-10">
                          <Sparkles className="w-3 h-3" />
                          AI Pick
                        </div>
                      )}
                    </div>
                    
                    {/* Theme Info */}
                    <div className="p-4">
                      <h3 className="font-bold text-lg text-zinc-900 mb-1">{theme.name}</h3>
                      <p className="text-sm text-zinc-500 line-clamp-2">{theme.description}</p>
                      
                      {/* Color Scheme Preview */}
                      {theme.color_scheme && (
                        <div className="flex gap-1.5 mt-3">
                          {/* Show palette colors if available, otherwise show primary/accent/secondary */}
                          {theme.color_scheme.palette?.length > 0 ? (
                            theme.color_scheme.palette.slice(0, 5).map((color, idx) => (
                              <div 
                                key={idx}
                                className="w-5 h-5 rounded-full border border-zinc-200 shadow-sm"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))
                          ) : (
                            <>
                              <div 
                                className="w-5 h-5 rounded-full border border-zinc-200 shadow-sm"
                                style={{ backgroundColor: theme.color_scheme.primary }}
                                title={`Primary: ${theme.color_scheme.primary}`}
                              />
                              <div 
                                className="w-5 h-5 rounded-full border border-zinc-200 shadow-sm"
                                style={{ backgroundColor: theme.color_scheme.accent }}
                                title={`Accent: ${theme.color_scheme.accent}`}
                              />
                              <div 
                                className="w-5 h-5 rounded-full border border-zinc-200 shadow-sm"
                                style={{ backgroundColor: theme.color_scheme.secondary }}
                                title={`Secondary: ${theme.color_scheme.secondary}`}
                              />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          
          {/* Load More Button */}
          {visibleCount < filteredThemes.length && (
            <div className="flex justify-center mb-12">
              <Button
                variant="outline"
                onClick={() => setVisibleCount(prev => prev + THEMES_PER_PAGE)}
                className="px-8 py-6 text-base font-medium text-zinc-700 hover:bg-zinc-100 border-zinc-300"
                data-testid="load-more-themes-btn"
              >
                <ChevronDown className="w-5 h-5 mr-2" />
                Load More Themes
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            {selectedTheme ? (
              <p className="text-sm text-zinc-600">
                Selected: <span className="font-semibold text-zinc-900">{themes.find(t => t.theme_id === selectedTheme)?.name}</span>
              </p>
            ) : selectedWpOrgTheme ? (
              <p className="text-sm text-zinc-600">
                Selected: <span className="font-semibold text-zinc-900">{selectedWpOrgTheme.name}</span>
                <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">WordPress.org</span>
              </p>
            ) : (
              <p className="text-sm text-zinc-500">Select a theme to continue</p>
            )}
          </div>
          <Button
            data-testid="generate-design-btn"
            onClick={handleGenerateDesign}
            disabled={(!selectedTheme && !selectedWpOrgTheme) || generating}
            className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating Design...
              </>
            ) : (
              <>
                Generate Design with AI
                <ChevronRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Spacer for fixed bottom bar */}
      <div className="h-24"></div>
      
      {/* Theme Preview Modal */}
      <ThemePreviewModal
        theme={previewTheme}
        isOpen={isPreviewOpen}
        onClose={closeThemePreview}
        onSelect={(themeId) => {
          // Check if it's a WordPress.org theme
          if (previewTheme?.is_wordpress_org) {
            // Set WordPress.org theme selection
            setSelectedWpOrgTheme(previewTheme);
            setSelectedTheme(null); // Clear library theme selection
          } else {
            // Set library theme selection
            setSelectedTheme(themeId);
            setSelectedWpOrgTheme(null); // Clear WordPress.org selection
          }
        }}
        isSelected={
          previewTheme?.is_wordpress_org 
            ? selectedWpOrgTheme?.slug === previewTheme?.slug
            : previewTheme?.theme_id === selectedTheme
        }
      />
    </div>
  );
};

export default ThemeSelectionPage;
